import type {
  BaseQueryFn,
  EndpointBuilder,
  FetchArgs,
} from "@reduxjs/toolkit/query/react";

import type { PricingModel } from "@superblocksteam/shared";

export type BillingInterval = "monthly" | "annual";

export type UpdateSubscriptionRequest = {
  unitType: "builder_seat" | "deployed_app" | "ai_credits";
  quantity: number;
  billingInterval?: BillingInterval;
  aiCreditPackSize?: number;
};

type UpdateSubscriptionApiResponse = {
  data: {
    message: string;
  };
};

export type UpdateSubscriptionResponse =
  UpdateSubscriptionApiResponse["data"] & {
    success?: boolean;
  };

export type PlanSummaryResponse = {
  activeAgents: number;
  billingInterval: BillingInterval;
  currentPlanCredits: number;
  currentUsageCredits: number;
  /** Top-up credits purchased this cycle (already folded into currentPlanCredits). */
  topupCreditsPurchased: number;
  /** Top-up credits used this cycle (already folded into currentUsageCredits). */
  topupCreditsUsed: number;
  cycleStart: string | null;
  cycleEnd: string | null;
  usageResetPeriodLimit: number;
  usageResetPeriodType: "annual" | "contract_term" | "monthly";
  creditsPerSeat: number | null;
  pricePerSeatAnnual: number | null;
  pricePerSeatMonthly: number | null;
  deployedAppsUsed: number;
  deployedAppsLimit: number;
  deployedAppsIncluded: number;
  deployedAppsAdditional: number;
  deployedAppPriceMonthly: number | null;
  deployedAppPriceAnnual: number | null;
  subscriptionStatus: string | null;
  // Stripe subscription id — consumed by the checkout E2E cleanup; not
  // used by the UI.
  stripeSubscriptionId: string | null;
  contractStart?: string | null;
  contractEnd?: string | null;
  contractType?: "annual_burn_down" | "dollar_commit" | "standard" | null;
  seatsUnlimited?: boolean;
  overageCreditsEnabled?: boolean;
  overageAppsEnabled?: boolean;
  overageSeatsEnabled?: boolean;
  dollarCommitAmountCents?: number | null;
  dollarCommitUsedCents?: number | null;
  deployedAppCostCents?: number | null;
  builderSeatsAssigned?: number;
  builderSeatsTotal?: number;
  pendingInterval: BillingInterval | null;
  pendingIntervalEffectiveAt: string | null;
  /**
   * Which pricing model the org is stamped with (`seats` legacy default,
   * `credits` = seatless PLG/GAU pricing). Server-authoritative — seat
   * surfaces hide and checkout flows switch to credit packs when `credits`.
   * Absent = seats. Typed via the shared union so a new pricing model can't
   * silently widen past this wire boundary.
   */
  pricingModel?: PricingModel;
  /**
   * Seatless trialing orgs only: the GAU pack held on the trialing
   * subscription (what the card renews onto). currentPlanCredits tracks the
   * LD trial allowance during a trial, so upgrade UIs read this instead.
   */
  committedPackCredits?: number | null;
  /**
   * True when the org has a Stripe customer with a chargeable default payment
   * method on file. Populated for POC plans so the plan banner can decide
   * between "Add credit card" vs "Manage"; false otherwise.
   */
  hasPaymentMethod?: boolean;
};

type PlanSummaryApiResponse = {
  data: PlanSummaryResponse;
};

type ReduxApiBuilder<ReducerPath extends string = string> = EndpointBuilder<
  BaseQueryFn<string | FetchArgs, unknown, unknown>,
  string,
  ReducerPath
>;

type BillingApiSlice = {
  util: {
    updateQueryData: (
      endpoint: string,
      arg: void,
      updateFn: (draft: unknown) => void,
    ) => { undo: () => void };
    invalidateTags: (tags: string[]) => unknown;
  };
  injectEndpoints: (config: unknown) => unknown;
};

export type PreviewInvoiceLineChange = {
  unitType: "builder_seat" | "deployed_app" | "ai_credits";
  quantity: number;
  aiCreditPackSize?: number;
};

export type PreviewInvoiceRequest = {
  changes: PreviewInvoiceLineChange[];
  billingInterval?: BillingInterval;
};

export type PreviewInvoiceResponse = {
  proratedAmountDue: number;
};

export type CreatePortalSessionRequest = {
  flow?: "payment_method_update" | "subscription_cancel";
  returnTo?: string;
};

export type CreatePortalSessionResponse = {
  portalUrl: string;
};

export type AutoTopupDisableReason =
  | "admin"
  | "card_declined"
  | "no_payment_method"
  | "plan_change";

export type AutoTopupConfigResponse = {
  enabled: boolean;
  pack_credits: number | null;
  monthly_credit_cap: number | null;
  trigger_buffer_credits: number | null;
  trigger_buffer_default: number;
  stats: {
    credits_purchased_this_cycle: number;
    credits_used_this_cycle: number;
    credits_remaining: number;
  } | null;
  unit_amount_cents: number;
  recent_topups: Array<{
    id: string;
    quantity_purchased: number;
    pool_credits_used: string;
    stripe_line_item_id: string;
    cycle_start: string;
    ended_at: string | null;
    /** 'auto' = auto-topup trigger; 'manual' = admin-initiated. */
    source: "auto" | "manual";
    /** Wall-clock timestamp the row was created (refill actually ran). UI renders this in the Refill history Date column instead of cycle_start, which is the org's billing-cycle anchor the topup row inherits — they diverge when a refill runs mid-cycle on a non-1st-of-month anchor. */
    created_at: string;
    /**
     * Who caused the refill row to exist. Distinct from `source`, which is
     * which billing pathway paid:
     *   - `'user'`     = customer admin clicked Buy → render "by &lt;name|email&gt;"
     *   - `'auto'`     = auto-topup trigger fired (also covers pre-feature NULL rows)
     *   - `'sb_grant'` = Superblocks staff gifted credits → render "Granted" pill,
     *                    Amount column shows `—` (no Stripe charge backs it).
     */
    initiator_kind: "user" | "auto" | "sb_grant";
    /** Display name of the initiator user when `initiator_kind === 'user'`. NULL for auto/sb_grant or if the initiator was deleted. UI prefers name over email but falls back to email when name is empty or equals email (the User entity defaults `name = email` on invite). */
    initiator_name: string | null;
    /** Email of the initiator user; same NULL semantics as `initiator_name`. */
    initiator_email: string | null;
  }>;
  has_default_payment_method: boolean;
  last_disabled_reason: AutoTopupDisableReason | null;
  last_disabled_at: string | null;
  available: boolean;
  unavailable_reason:
    | "plan_ineligible"
    | "trialing"
    | "canceling"
    | "canceled"
    | "feature_disabled"
    | "no_payment_method"
    | null;
  /** True when monthly cap has been reached this cycle and the next trigger fire would skip due to the partial-topup floor. UI surfaces an amber pill + banner so admins know auto top-up is paused (vs. broken). */
  cap_skip_pending: boolean;
  /** Server-side floor used to compute `cap_skip_pending`. Echoed so the banner copy doesn't hardcode the threshold. */
  min_partial_topup: number;
};

export type UpdateAutoTopupConfigBody = {
  enabled: boolean;
  packCredits: number | null;
  monthlyCreditCap: number | null;
  triggerBufferCredits: number | null;
};

export type UpdateAutoTopupConfigResponse = { ok: true };

export type ManualTopupChargeBody = {
  credits: number;
  /** Optional client UUID to make repeated submissions idempotent. */
  idempotencyKey?: string;
};

export type ManualTopupChargeResponse = {
  status: "succeeded" | "pending_authentication" | "failed";
  credits: number;
  stripeInvoiceId?: string;
  failureCode?: "card_declined" | "no_payment_method" | "stripe_error";
  failureMessage?: string;
};

// Auto-topup dev tools — staging/EE only. The endpoint 404s on production
// (no SUPERBLOCKS_DEV_TOOLS_ENABLED env var). Used by the AutoTopUpDevToolsModal.
export type AutoTopupDevToolsRequest = {
  action: string;
  params?: Record<string, unknown>;
};

export type AutoTopupDevToolsResponse = {
  ok: true;
  message: string;
  details?: unknown;
};

export type ChangeIntervalRequest = {
  billingInterval: BillingInterval;
};

export type ChangeIntervalResponse = {
  effectiveAt: string | null;
};

type BillingEndpointsApi = {
  useGetPlanSummaryQuery: unknown;
  useUpdateSubscriptionMutation: unknown;
  useCreatePortalSessionMutation: unknown;
  usePreviewInvoiceMutation: unknown;
  useGetAutoTopupConfigQuery: unknown;
  useUpdateAutoTopupConfigMutation: unknown;
  useManualTopupChargeMutation: unknown;
  useAutoTopupDevToolsMutation: unknown;
  useChangeIntervalMutation: unknown;
};

const PlanSummaryTag = "PlanSummary" as const;
const BuilderSeatsTag = "BuilderSeats" as const;
const AutoTopupConfigTag = "AutoTopupConfig" as const;

/**
 * Injects billing endpoints (getPlanSummary, updateSubscription) into your API slice.
 * Includes optimistic updates for seat/credit changes and delayed refetch for Stripe webhook.
 *
 * @param api - Your reduxApiSlice (must have listBuilderSeats and getPlanSummary already injected)
 */
export function injectBillingEndpoints(api: unknown): BillingEndpointsApi {
  const slice = api as BillingApiSlice;
  return slice.injectEndpoints({
    endpoints: (builder: unknown) =>
      buildBillingEndpoints(builder as ReduxApiBuilder, slice),
    overrideExisting: false,
  }) as BillingEndpointsApi;
}

function buildBillingEndpoints<ReducerPath extends string>(
  builder: ReduxApiBuilder<ReducerPath>,
  api: BillingApiSlice,
) {
  return {
    getPlanSummary: builder.query<PlanSummaryResponse, void>({
      query: () => "v1/billing/plan-summary",
      transformResponse: (response: PlanSummaryApiResponse) => response.data,
      providesTags: [PlanSummaryTag],
    }),
    createPortalSession: builder.mutation<
      CreatePortalSessionResponse,
      CreatePortalSessionRequest
    >({
      query: (body) => ({
        url: "v1/billing/create-portal-session",
        method: "POST",
        body,
      }),
      transformResponse: (response: { data: CreatePortalSessionResponse }) =>
        response.data,
    }),
    previewInvoice: builder.mutation<
      PreviewInvoiceResponse,
      PreviewInvoiceRequest
    >({
      query: (body) => ({
        url: "v1/billing/preview-invoice",
        method: "POST",
        body,
      }),
      transformResponse: (response: { data: PreviewInvoiceResponse }) =>
        response.data,
    }),
    updateSubscription: builder.mutation<
      UpdateSubscriptionResponse,
      UpdateSubscriptionRequest
    >({
      query: (body) => ({
        url: "v1/billing/update-subscription",
        method: "POST",
        body,
      }),
      transformResponse: (response: UpdateSubscriptionApiResponse) =>
        response.data,
      invalidatesTags: [],
      ...{
        async onQueryStarted(
          arg: UpdateSubscriptionRequest,
          {
            dispatch,
            queryFulfilled,
          }: {
            dispatch: (action: unknown) => unknown;
            queryFulfilled: Promise<unknown>;
          },
        ) {
          const patchResults: { undo: () => void }[] = [];

          if (arg.unitType === "builder_seat") {
            const patch = dispatch(
              api.util.updateQueryData(
                "listBuilderSeats",
                undefined,
                (draft) => {
                  (
                    draft as { totalPurchasedSeats: number }
                  ).totalPurchasedSeats = arg.quantity;
                },
              ),
            ) as { undo: () => void };
            patchResults.push(patch);
          }

          if (arg.unitType === "ai_credits" && arg.aiCreditPackSize != null) {
            const creditsToAdd = arg.aiCreditPackSize * arg.quantity;
            const patch = dispatch(
              api.util.updateQueryData("getPlanSummary", undefined, (draft) => {
                (draft as { currentPlanCredits: number }).currentPlanCredits =
                  creditsToAdd;
              }),
            ) as { undo: () => void };
            patchResults.push(patch);
          }

          // Downgrade-to-zero sends quantity:0 and no aiCreditPackSize;
          // the branch above is gated on aiCreditPackSize and would skip
          // the optimistic patch entirely, so the dashboard would keep
          // rendering the old recurring quantity until the deferred
          // invalidate refetch lands. Patch the recurring portion to 0
          // and preserve the topup fold so the UI matches what the user
          // just committed to.
          if (arg.unitType === "ai_credits" && arg.quantity === 0) {
            const patch = dispatch(
              api.util.updateQueryData("getPlanSummary", undefined, (draft) => {
                const d = draft as {
                  currentPlanCredits: number;
                  topupCreditsPurchased?: number;
                };
                d.currentPlanCredits = d.topupCreditsPurchased ?? 0;
              }),
            ) as { undo: () => void };
            patchResults.push(patch);
          }

          if (arg.unitType === "deployed_app") {
            const patch = dispatch(
              api.util.updateQueryData("getPlanSummary", undefined, (draft) => {
                (
                  draft as { deployedAppsAdditional: number }
                ).deployedAppsAdditional = arg.quantity;
              }),
            ) as { undo: () => void };
            patchResults.push(patch);
          }

          try {
            await queryFulfilled;
            const tagsToInvalidate =
              arg.unitType === "ai_credits"
                ? [PlanSummaryTag]
                : [BuilderSeatsTag, PlanSummaryTag];
            // After update-subscription returns, the entitlement row only
            // gets upserted by the customer.subscription.updated webhook.
            // Stripe → app webhook delivery is usually a few hundred ms
            // but can exceed 1s on cold pods or slow ingress; refetching
            // before the upsert lands overwrites the optimistic patch
            // with the stale value and the tile flashes back to the prior
            // recurring quantity. 3s comfortably covers the tail.
            setTimeout(() => {
              void dispatch(api.util.invalidateTags(tagsToInvalidate));
            }, 3000);
          } catch {
            for (const p of patchResults) p.undo();
          }
        },
      },
    }),
    getAutoTopupConfig: builder.query<AutoTopupConfigResponse, void>({
      query: () => "v1/billing/auto-topup",
      providesTags: [AutoTopupConfigTag],
    }),
    updateAutoTopupConfig: builder.mutation<
      UpdateAutoTopupConfigResponse,
      UpdateAutoTopupConfigBody
    >({
      query: (body) => ({
        url: "v1/billing/auto-topup",
        method: "PATCH",
        body,
      }),
      invalidatesTags: [AutoTopupConfigTag],
    }),
    manualTopupCharge: builder.mutation<
      ManualTopupChargeResponse,
      ManualTopupChargeBody
    >({
      query: (body) => ({
        url: "v1/billing/auto-topup/manual-charge",
        method: "POST",
        body,
      }),
      // After a successful charge, both the auto-topup panel (recent_topups
      // grows) and the plan summary (topup credits totals) need refreshing.
      invalidatesTags: [AutoTopupConfigTag, PlanSummaryTag],
    }),
    // Auto-topup dev tools mutation — staging/EE only at runtime; the
    // server endpoint 404s on prod (no SUPERBLOCKS_DEV_TOOLS_ENABLED).
    autoTopupDevTools: builder.mutation<
      AutoTopupDevToolsResponse,
      AutoTopupDevToolsRequest
    >({
      query: (body) => ({
        url: "v1/billing/auto-topup/dev-tools",
        method: "POST",
        body,
      }),
      // Dev-tools mutations can touch pool, per-user seat, topups, sub status,
      // cancel_at, the org_contract row, and dollar_commit_ledger — any of
      // which feed into cached queries elsewhere in the app. Invalidate
      // aggressively (literal "UsageRecords" — that tag is exported from
      // usageRecords.ts and we can't import it here without circling, but
      // both sides use the same string under the hood) so the UI reflects
      // DB state immediately on /spending + /usage.
      invalidatesTags: [
        AutoTopupConfigTag,
        BuilderSeatsTag,
        PlanSummaryTag,
        "UsageRecords",
      ],
    }),
    changeInterval: builder.mutation<
      ChangeIntervalResponse,
      ChangeIntervalRequest
    >({
      query: (body) => ({
        url: "v1/billing/change-interval",
        method: "POST",
        body,
      }),
      transformResponse: (response: { data: ChangeIntervalResponse }) =>
        response.data,
      // The handler writes pending_interval before returning 200, so a single
      // invalidate on success is enough — no webhook race like the seats /
      // credits flows have. The Plan Summary refetch lands within ~100ms.
      invalidatesTags: [PlanSummaryTag],
    }),
  };
}
